<script setup lang="ts">
import { computed } from 'vue';

import { NutsProduct } from '@/utils/product';
import { Admin } from '@/views/PDP.vue';

const props = defineProps<{
  bagSizes: Admin['bagSizes'];
  inventory: Admin['inventory'];
  productKey: NutsProduct['key'];
}>();

const hasAdminData = computed(
  () => props.bagSizes.length > 0 || Object.entries(props.inventory).length > 0,
);

const formattedBagSizes = props.bagSizes.join(', ');

const formattedInventory = computed(() => {
  const inventoryLines: string[] = [];

  if (props.inventory) {
    Object.keys(props.inventory).forEach((warehouse) => {
      const countsPerSku = props.inventory[warehouse];
      const joinedCounts = Object.keys(countsPerSku).map((sku) => `${sku}: ${countsPerSku[sku]}`);
      inventoryLines.push(`${warehouse}: ${joinedCounts.join(', ')}`);
    });
  }

  return inventoryLines;
});
</script>

<template>
  <div v-if="hasAdminData" class="p-2.5 bg-lime-100" data-test="internal-notes">
    <p class="text-base font-semibold uppercase font-proxima-nova md:text-lg">
      Only visible to employees
    </p>
    <p
      v-if="bagSizes.length > 0"
      class="mt-1 text-base font-normal large-text md:text-lg md:leading-6 font-proxima-nova"
      data-test="bag-sizes-viewer"
    >
      Bag Sizes: {{ formattedBagSizes }}
    </p>
    <div v-if="formattedInventory.length" data-test="inventory-viewer">
      <p
        v-for="(inventory, index) in formattedInventory"
        :key="index"
        class="text-base font-normal large-text md:text-lg md:leading-6 font-proxima-nova"
      >
        {{ inventory }}
      </p>
    </div>
    <div class="mt-0.5" data-test="non-retail-inventory-viewer">
      <a
        :href="`https://nj.kw.nuts.com/items/${productKey}/piece_inventory?active_tab=${productKey}`"
        target="_blank"
      >
        <span class="underline text-cyan-800"> New Jersey Non-Retail Inventory </span>
      </a>
    </div>
  </div>
</template>

<style scoped>
p {
  @apply mb-0;
}
</style>
